import { useCallback, useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { CHAT_DOCK_LANDING_REVEAL_MS } from '@/common/motion/chatDockMotion';
// Shared dock-shell styling. DailySummaryModal.css owns the `.daily-summary-*`
// shell classes this component emits; it's imported here (the live consumer)
// rather than from DailySummaryModal.tsx, which is currently parked/unwired
// (Daily Brief is de-integrated — to be reintegrated via the Agents module).
import '../../layout/DailySummaryModal.css';

/**
 * Shared docked-card shell for the header-bell dock. Emits the existing
 * `.daily-summary-*` classes + `id="daily-brief-panel"` so all current dock
 * positioning, the bell flight, the View-Transition morphs, and the flat-theme
 * polish (DailySummaryModal.css) apply unchanged. The Activity Center renders
 * through this shell; the parked Daily Brief (DailySummaryModal.tsx) reuses it
 * on reintegration.
 */
export type DockPanelState = 'closed' | 'docking' | 'open' | 'undocking';

export function DockPanel({
  title,
  children,
  onClose,
  isOpen = true,
  mode = 'panel',
  dockState,
  cardRef,
  landingReveal = false,
  onLandingRevealComplete,
  headerActions,
  ariaLabel,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  isOpen?: boolean;
  mode?: 'modal' | 'panel';
  dockState?: DockPanelState;
  cardRef?: RefObject<HTMLDivElement | null>;
  landingReveal?: boolean;
  onLandingRevealComplete?: () => void;
  headerActions?: ReactNode;
  ariaLabel?: string;
}) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const setCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (cardRef) cardRef.current = node;
    },
    [cardRef],
  );

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (landingReveal) closeButtonRef.current?.focus();
  }, [landingReveal]);

  useEffect(() => {
    if (!landingReveal || !onLandingRevealComplete) return;
    const t = window.setTimeout(onLandingRevealComplete, CHAT_DOCK_LANDING_REVEAL_MS);
    return () => window.clearTimeout(t);
  }, [landingReveal, onLandingRevealComplete]);

  useEffect(() => {
    if (isOpen && mode === 'modal') {
      document.body.classList.add('modal-open');
      return () => document.body.classList.remove('modal-open');
    }
  }, [isOpen, mode]);

  const dockClass = [
    dockState === 'docking' ? ' brief-dock-docking' : '',
    dockState === 'undocking' ? ' brief-dock-undocking' : '',
    landingReveal ? ' brief-dock-landing' : '',
  ].join('');

  const card = (
    <div
      ref={setCardRef}
      id="daily-brief-panel"
      role="dialog"
      aria-modal={mode === 'modal' ? true : undefined}
      aria-label={ariaLabel ?? title}
      className={`daily-summary-modal${mode === 'panel' ? ' daily-summary-panel-card' : ''}${dockClass}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="daily-summary-header">
        <div className="daily-summary-header-left">
          <h2 className="daily-summary-title">{title}</h2>
        </div>
        <div className="daily-summary-header-right">
          {headerActions}
          <button
            ref={closeButtonRef}
            type="button"
            className="daily-summary-close"
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <div className="daily-summary-body xv-scroll">{children}</div>
    </div>
  );

  if (mode === 'panel') return card;

  return createPortal(
    <div className="daily-summary-overlay" role="presentation" onClick={onClose}>
      {card}
    </div>,
    document.body,
  );
}
